import React, { useState, useEffect } from 'react';
import { getMemberReport, getContributionReport } from '../../../services/reports';
import DataTable from '../../../components/Tables/DataTable';
import { formatCurrency } from '../../../utils/formatters';
import { 
  Users, 
  Coins, 
  FilePieChart, 
  Download, 
  AlertTriangle,
  CheckCircle2,
  Clock,
  TrendingUp,
  ShieldCheck,
  Zap
} from 'lucide-react';

const GeneralReports = () => {
  const [members, setMembers] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const [memberData, contributionData] = await Promise.all([
        getMemberReport(),
        getContributionReport(),
      ]);
      setMembers(Array.isArray(memberData) ? memberData : []);
      setContributions(contributionData?.contributions || []);
    } catch (err) {
      console.error("Master Report Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const memberColumns = [
    { 
      key: 'member_number', 
      label: 'ID',
      render: (_, row) => <span className="font-mono text-[10px] font-black text-[#162f68] bg-[#162f68]/5 px-2 py-1 rounded-md border border-[#162f68]/10">{row?.member_number}</span>
    },
    { 
      key: 'full_name', 
      label: 'Full Name', 
      render: (_, row) => {
        const name = row?.full_name || row?.User?.full_name || 'System User';
        return (
          <div className="flex flex-col">
            <span className="font-black text-slate-900 leading-none">{name}</span>
            <span className="text-[10px] text-slate-400 mt-1 uppercase tracking-tighter">Member Account</span>
          </div>
        );
      }
    },
    { 
      key: 'email', 
      label: 'Contact Information', 
      render: (_, row) => {
        const email = row?.email || row?.User?.email || 'No Email';
        return <span className="text-slate-500 text-xs font-medium">{email.toLowerCase()}</span>;
      }
    },
    { 
      key: 'status', 
      label: 'Standing',
      render: (_, row) => {
        const status = row?.status || 'inactive';
        return (
          <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
            status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-rose-50 text-[#ff1901] border-rose-100'
          }`}>
            {status}
          </span>
        );
      }
    },
    { 
      key: 'missed_pay_count', 
      label: 'Delinquency',
      render: (_, row) => {
        const count = row?.missed_pay_count || 0;
        return (
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs ${count > 0 ? 'bg-rose-50 text-[#ff1901]' : 'bg-slate-50 text-slate-400'}`}>
              {count}
            </div>
            {count > 0 && <AlertTriangle size={14} className="text-[#ff1901] animate-pulse" />}
          </div>
        );
      }
    },
  ];

  const contributionColumns = [
    { 
      key: 'member.member_number', 
      label: 'Ref No.',
      render: (_, row) => {
        const mNum = row?.Member?.member_number || row?.member?.member_number;
        return <span className="font-mono font-black text-[#162f68]">{mNum}</span>;
      }
    },
    { 
      key: 'case.type', 
      label: 'Contribution Type',
      render: (_, row) => {
        const type = row.Case?.type?.replace('_', ' ') || row.case?.type?.replace('_', ' ') || 'Direct';
        return (
          <span className="text-[9px] font-black uppercase tracking-[0.1em] text-[#015bbe] bg-[#015bbe]/5 px-3 py-1.5 rounded-full border border-[#015bbe]/10">
            {type}
          </span>
        );
      }
    },
    { 
      key: 'amount', 
      label: 'Value', 
      render: (_, row) => <span className="font-black text-slate-900 text-base">{formatCurrency(row?.amount)}</span> 
    },
    { 
      key: 'paid_at', 
      label: 'Processing Date', 
      render: (_, row) => (
        <div className="flex items-center gap-2 text-slate-400 font-bold text-[10px] uppercase tracking-tighter">
          <Clock size={12} strokeWidth={3} />
          {row?.paid_at ? new Date(row.paid_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Pending'}
        </div>
      ) 
    },
  ];

  // Logic for the Summary KPIs
  const totalCollected = contributions.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

  return (
    <div className="p-6 lg:p-12 max-w-[1400px] mx-auto space-y-10 animate-in fade-in duration-700">
      
      {/* 1. TOP HEADER SECTION */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#162f68] rounded-xl flex items-center justify-center shadow-lg shadow-[#162f68]/20">
              <FilePieChart size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-[#162f68] tracking-tight leading-none">General Reports</h1>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-[0.2em] mt-2">Nairobi Yearly Meeting Ledger</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
           <button className="flex items-center gap-2 px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-[10px] font-black uppercase tracking-widest text-[#162f68] hover:bg-white hover:shadow-lg transition-all active:scale-95">
             <Download size={14} strokeWidth={3} /> Export Data
           </button>
        </div>
      </div>

      {/* 2. KPI INSIGHT CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 flex items-center justify-between group hover:border-[#eda006] transition-colors">
          <div className="space-y-1">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Membership</p>
            <h3 className="text-4xl font-black text-[#162f68] tracking-tighter">{members.length}</h3>
          </div>
          <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-300 group-hover:text-[#eda006] transition-colors">
            <Users size={28} />
          </div>
        </div>

        <div className="bg-[#162f68] p-8 rounded-[2rem] text-white flex items-center justify-between shadow-2xl shadow-[#162f68]/20">
          <div className="space-y-1">
            <p className="text-[10px] font-black text-[#2baae1] uppercase tracking-widest">Revenue (Last 20)</p>
            <h3 className="text-3xl font-black tracking-tighter">{formatCurrency(totalCollected)}</h3>
          </div>
          <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center text-[#eda006]">
            <TrendingUp size={28} />
          </div>
        </div>

        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 flex items-center justify-between group hover:border-[#ff1901] transition-colors">
          <div className="space-y-1">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">System Health</p>
            <h3 className="text-xl font-black text-slate-900 tracking-tight">Verified</h3>
          </div>
          <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-500">
            <ShieldCheck size={28} />
          </div>
        </div>
      </div>

      {/* 3. TAB NAVIGATION - Sleeker Pill Design */}
      <div className="flex justify-center">
        <div className="flex gap-2 bg-slate-100 p-1.5 rounded-2xl shadow-inner border border-slate-200/50">
          {[
            { id: 'members', label: 'Member Summary', icon: <Users size={16} /> },
            { id: 'contributions', label: 'Recent Transactions', icon: <Coins size={16} /> }
          ].map((tab) => (
            <button 
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-3 px-8 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${
                activeTab === tab.id ? 'bg-[#162f68] text-white shadow-xl' : 'text-slate-500 hover:text-[#162f68]'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 4. TABLE AREA */}
      <div className="bg-white rounded-[3rem] border border-slate-100 shadow-[0_40px_80px_-20px_rgba(22,47,104,0.05)] overflow-hidden">
        <div className="px-10 py-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
          <h3 className="font-black text-[#162f68] flex items-center gap-3 text-lg">
            {activeTab === 'members' ? (
              <><CheckCircle2 size={20} className="text-emerald-500" /> Current Membership Roll</>
            ) : (
              <><Coins size={20} className="text-[#eda006]" /> Transaction Log History</>
            )}
          </h3>
          <span className="text-[9px] font-black text-slate-300 bg-white border border-slate-100 px-3 py-1 rounded-full uppercase tracking-widest">
            {activeTab === 'members' ? `${members.length} Enrolled` : '20 Record View'}
          </span>
        </div>
        
        <div className="overflow-x-auto">
          {activeTab === 'members' ? (
            <div className="animate-in fade-in slide-in-from-left-4 duration-500">
               <DataTable columns={memberColumns} data={members} loading={loading} />
            </div>
          ) : (
            <div className="animate-in fade-in slide-in-from-right-4 duration-500">
               <DataTable columns={contributionColumns} data={contributions.slice(0, 20)} loading={loading} />
            </div>
          )}
        </div>
      </div>

      {/* 5. FOOTER INSIGHTS */}
      <div className="bg-[#162f68]/5 border border-[#162f68]/10 rounded-[2rem] p-8 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-[#eda006] rounded-full flex items-center justify-center text-[#162f68]">
            <Zap size={20} fill="currentColor" />
          </div>
          <div>
            <p className="text-[10px] font-black text-[#162f68] uppercase tracking-widest">Proactive Audit Status</p>
            <p className="text-sm font-medium text-slate-500 italic leading-snug">The ledger is currently balanced with zero unallocated contributions.</p>
          </div>
        </div>
        <p className="text-[9px] font-black text-slate-300 uppercase tracking-[0.4em]">Quakermen Ledger v3.0</p>
      </div>

    </div>
  );
};

export default GeneralReports;